import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL;

const BrandDivider = () => (
  <div className="flex items-center justify-center gap-3 my-10">
    <div className="h-px flex-1 bg-[#00FF41]/20" />
    <span className="font-mono font-black text-[#00FF41] text-sm tracking-tight" style={{ textShadow: '0 0 10px rgba(0,255,65,0.3)' }}>
      &lt;/AI_covo.tech&gt;<sup style={{ fontSize: '0.5em', verticalAlign: 'super', marginLeft: '1px', fontWeight: 400, fontFamily: 'Georgia, serif' }}>&reg;</sup>
    </span>
    <div className="h-px flex-1 bg-[#00FF41]/20" />
  </div>
);

const exampleQuestions = [
  'Perché il cielo è azzurro?',
  "Quanto è grande l'universo?",
  'Come funziona il Wi-Fi?',
  'Chi ha inventato la pizza?',
  'Perché le foglie cambiano colore?',
  "Cos'è un buco nero?",
];

const stickerPrices = [
  { qty: '1 adesivo', price: '3,99€', desc: 'Posizionalo dove vuoi: lampione, fontanello, fermata bus' },
  { qty: '3 adesivi', price: '9,99€', desc: 'Copri il tuo quartiere: scuola, parco, piazza', popular: true },
  { qty: '10 adesivi', price: '19,99€', desc: 'Diventa ambasciatore della conoscenza nella tua città' },
];

const MentorPage = () => {
  const { theme } = useTheme();
  const isLight = theme === 'istituzionale';
  const mentorColor = isLight ? '#FF6B35' : '#00FF41';
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [stats, setStats] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [giftStyle, setGiftStyle] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/mentor/stats`).then(r => r.json()).then(setStats).catch(() => {});
  }, [messages.length]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const askQuestion = async (q) => {
    const question = q || input.trim();
    if (!question || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: question }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/mentor/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, session_id: sessionId }),
      });
      const data = await res.json();
      if (res.ok) {
        setSessionId(data.session_id);
        setMessages(prev => [...prev, { role: 'mentor', text: data.answer }]);
      } else {
        setMessages(prev => [...prev, { role: 'mentor', text: `Errore: ${data.detail || 'Riprova.'}` }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'mentor', text: 'Errore di connessione. Riprova.' }]);
    } finally { setLoading(false); }
  };

  const bgCard = isLight ? 'bg-[#FAF9F6] border-[#E2DED6]' : 'bg-[#0A0A0A] border-zinc-800';
  const textMain = isLight ? 'text-[#1B2A4A]' : 'text-white';
  const textSub = isLight ? 'text-[#5A6B80]' : 'text-zinc-400';
  const textMuted = isLight ? 'text-[#8A7A62]' : 'text-zinc-600';
  const borderAccent = isLight ? 'border-[#FF6B35]' : 'border-[#00FF41]';

  return (
    <main data-testid="mentor-page" className="pt-20 pb-8">
      <div className="max-w-4xl mx-auto px-4">

        {/* HERO */}
        <div className="text-center mb-8">
          <h1 className="text-6xl md:text-8xl font-black mb-2" data-testid="mentor-title" style={{
            color: mentorColor,
            textShadow: isLight ? 'none' : `0 0 40px ${mentorColor}66, 0 0 80px ${mentorColor}22`,
            fontFamily: "'Georgia', serif",
          }}>
            Μέντωρ
          </h1>
          <p className={`text-xl md:text-2xl font-black mb-2 ${textMain}`}>per tutti, dappertutto!</p>
          <p className={`text-sm font-mono tracking-wider mb-1 ${textSub}`}>IL TUO INFO POINT DIGITALE GRATUITO</p>
          <p className={`text-xs ${textMuted}`}>Powered by AI — Un progetto <strong className="font-mono font-black" style={{ color: mentorColor }}>&lt;/AI_covo.tech&gt;</strong><sup style={{ fontSize: '0.6em', fontFamily: 'Georgia, serif' }}>&reg;</sup></p>
          {stats && stats.total_questions > 0 && (
            <p className="text-[10px] font-mono mt-2" style={{ color: mentorColor }}>{stats.total_questions} domande risposte finora</p>
          )}
          <div className="mt-3 flex gap-3 justify-center">
            <a href="/qr_mentore.png" download="QR_Mentore_Sapienza.png" data-testid="download-qr-mentore"
              className="font-mono text-xs px-3 py-1.5 rounded-full border transition-colors"
              style={{ borderColor: mentorColor + '44', color: mentorColor }}>
              Scarica QR Code
            </a>
            <a href="/logo_aicovo_transparent.png" download="AI_covo_tech_logo.png"
              className="font-mono text-xs px-3 py-1.5 rounded-full border transition-colors"
              style={{ borderColor: mentorColor + '44', color: mentorColor }}>
              Scarica Logo
            </a>
          </div>
        </div>

        {/* CHAT */}
        <div className={`${bgCard} border rounded-2xl overflow-hidden mb-4`} data-testid="mentor-chat">
          <div className="overflow-y-auto p-4 space-y-4 min-h-[250px] max-h-[45vh]">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8">
                <p className="text-4xl mb-3" style={{ fontFamily: "'Georgia', serif", color: mentorColor }}>Μ</p>
                <p className={`text-sm text-center mb-4 ${textSub}`}>Chiedimi qualsiasi cosa. Sono qui per rispondere.</p>
                <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                  {exampleQuestions.map((q, i) => (
                    <button key={i} data-testid={`example-q-${i}`} onClick={() => askQuestion(q)}
                      className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
                        isLight ? 'border-[#E2DED6] text-[#5A6B80] hover:border-[#FF6B35] hover:text-[#FF6B35]' : 'border-zinc-700 text-zinc-400 hover:border-[#00FF41]/50 hover:text-[#00FF41]'
                      }`}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div data-testid={`msg-${i}`} className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  m.role === 'user'
                    ? (isLight ? 'bg-[#FF6B35]/10 border border-[#FF6B35]/20 text-[#1B2A4A]' : 'bg-[#00FF41]/10 border border-[#00FF41]/20 text-white')
                    : (isLight ? 'bg-white border border-[#E2DED6] text-[#3A3020]' : 'bg-zinc-900 border border-zinc-800 text-zinc-300')
                }`}>
                  {m.role === 'mentor' && <p className="text-[10px] font-mono mb-1 font-bold" style={{ color: mentorColor }}>Μέντωρ</p>}
                  <div className="text-sm leading-relaxed whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: m.text.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" style="color:' + mentorColor + ';text-decoration:underline;word-break:break-all">$1</a>') }} />
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className={`${isLight ? 'bg-white border-[#E2DED6]' : 'bg-zinc-900 border-zinc-800'} border rounded-2xl px-4 py-3`}>
                  <p className="text-[10px] font-mono mb-1 font-bold" style={{ color: mentorColor }}>Μέντωρ</p>
                  <div className="flex gap-1.5">
                    {[0, 150, 300].map(d => <span key={d} className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: mentorColor, animationDelay: `${d}ms` }} />)}
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <div className={`border-t ${isLight ? 'border-[#E2DED6]' : 'border-zinc-800'} p-3 flex gap-2`}>
            <input data-testid="mentor-input" type="text" value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); askQuestion(); } }}
              placeholder="Chiedimi qualcosa..." disabled={loading}
              className={`flex-1 border rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors disabled:opacity-50 ${
                isLight ? 'bg-white border-[#E2DED6] text-[#1B2A4A] placeholder-[#8A7A62] focus:border-[#FF6B35]/50' : 'bg-zinc-900 border-zinc-700 text-white placeholder-zinc-600 focus:border-[#00FF41]/50'
              }`} />
            <button data-testid="mentor-send" onClick={() => askQuestion()} disabled={loading || !input.trim()}
              className="px-5 py-3 font-bold rounded-xl transition-colors disabled:opacity-30 text-sm"
              style={{ backgroundColor: mentorColor, color: isLight ? '#fff' : '#000' }}>
              Chiedi
            </button>
          </div>
        </div>

        <div className="flex gap-3 mb-6">
          <button data-testid="mentor-show-qr" onClick={() => setShowQR(!showQR)}
            className={`px-3 py-1.5 rounded-full border text-xs font-mono transition-colors ${isLight ? 'border-[#E2DED6] text-[#5A6B80] hover:border-[#FF6B35] hover:text-[#FF6B35]' : 'border-zinc-700 text-zinc-400 hover:border-[#00FF41]/50 hover:text-[#00FF41]'}`}>
            {showQR ? 'Chiudi QR' : 'Mostra QR Code'}
          </button>
          <button onClick={() => { setMessages([]); setSessionId(null); }} data-testid="mentor-reset"
            className={`px-3 py-1.5 rounded-full border text-xs font-mono transition-colors ${isLight ? 'border-[#E2DED6] text-[#5A6B80] hover:text-[#1B2A4A]' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}>
            Nuova conversazione
          </button>
        </div>

        {showQR && (
          <div className={`${bgCard} border rounded-2xl p-6 text-center mb-6 ${borderAccent}`} data-testid="mentor-qr-panel">
            <img src="/mentor_qr.png" alt="QR Code Μέντωρ" className="w-48 h-48 mx-auto mb-4" />
            <p className={`text-xs mb-3 ${textSub}`}>Scansiona per accedere a Μέντωρ</p>
            <a href="/mentor_poster.png" download="mentor_poster_aicovo.png" data-testid="mentor-download-poster"
              className="inline-block px-4 py-2 rounded-lg text-xs font-mono transition-colors"
              style={{ backgroundColor: `${mentorColor}15`, border: `1px solid ${mentorColor}44`, color: mentorColor }}>
              Scarica poster A4
            </a>
          </div>
        )}

        <BrandDivider />

        {/* PROGETTO SCUOLE */}
        <div className="text-center mb-10">
          <p className="font-mono text-xs uppercase tracking-[0.2em] mb-3" style={{ color: mentorColor }}>Progetto Scuole</p>
          <h2 className={`text-3xl md:text-5xl font-black mb-3 ${textMain}`}>
            Un <span style={{ fontFamily: "'Georgia', serif", color: mentorColor }}>Μέντωρ</span> per tutti,<br/>dappertutto!
          </h2>
          <p className={`text-sm max-w-2xl mx-auto mb-2 ${textSub}`}>
            Sostieni il progetto acquistando un <strong className={textMain}>adesivo QR Code Stencil</strong> da lasciare
            in palestra, nel parco della scuola, su un lampione, alla fermata dell'autobus,
            ai giardini pubblici, al fontanello dell'acqua pubblica.
          </p>
          <p className={`text-xs font-mono ${textMuted}`}>
            Ogni adesivo è un punto di accesso gratuito alla conoscenza per chiunque passi.
          </p>
        </div>

        {/* AI Images con CSS overlay Μέντωρ */}
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          {[
            { img: '/mentor_sticker_park.png', title: 'Parco pubblico', sub: 'Un lampione diventa un info point' },
            { img: '/mentor_genz_promote.png', title: 'Gen Z + Beta in azione', sub: 'La conoscenza si diffonde dal basso' },
            { img: '/mentor_sala.png', title: 'Sala Μέντωρ', sub: 'Il quartier generale della sapienza' },
          ].map((item, i) => (
            <div key={i} className={`rounded-xl overflow-hidden border ${isLight ? 'border-[#E2DED6]' : 'border-zinc-800'}`}>
              <div className="relative h-56 overflow-hidden">
                <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="font-black text-3xl" style={{
                    fontFamily: "'Georgia', serif",
                    color: '#00FF41',
                    textShadow: '0 0 20px rgba(0,255,65,0.6), 0 0 40px rgba(0,255,65,0.3), 2px 2px 0 #000',
                    WebkitTextStroke: '1px rgba(0,0,0,0.5)',
                    paintOrder: 'stroke fill',
                  }}>Μέντωρ</span>
                  <span className="text-white/80 text-[10px] font-mono mt-1 tracking-wider" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>per tutti, dappertutto!</span>
                </div>
              </div>
              <div className={`p-3 ${isLight ? 'bg-[#FAF9F6]' : 'bg-[#0A0A0A]'}`}>
                <p className={`text-xs font-bold ${textMain}`}>{item.title}</p>
                <p className={`text-[10px] ${textMuted}`}>{item.sub}</p>
              </div>
            </div>
          ))}
        </div>

        <BrandDivider />

        {/* PRICING ADESIVI */}
        <div className="text-center mb-8">
          <p className="font-mono text-xs uppercase tracking-[0.2em] mb-3" style={{ color: mentorColor }}>Acquista gli adesivi</p>
          <h2 className={`text-2xl md:text-3xl font-black ${textMain}`}>
            Diffondi la conoscenza. <span style={{ color: mentorColor }}>Letteralmente.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {stickerPrices.map((s, i) => (
            <div key={i} data-testid={`sticker-${i}`}
              className={`relative ${bgCard} border-2 rounded-xl p-6 text-center transition-all hover:scale-105 ${
                s.popular ? borderAccent : (isLight ? 'border-[#E2DED6]' : 'border-zinc-800')
              }`}>
              {s.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 font-black text-[10px] px-3 py-1 rounded-full"
                  style={{ backgroundColor: mentorColor, color: isLight ? '#fff' : '#000' }}>
                  PIU' SCELTO
                </div>
              )}
              <p className={`font-bold text-lg mb-1 ${textMain}`}>{s.qty}</p>
              <p className="font-black text-3xl mb-2" style={{ color: mentorColor }}>{s.price}</p>
              <p className={`text-xs mb-4 ${textSub}`}>{s.desc}</p>
              <Link to="/dona" data-testid={`sticker-buy-${i}`}
                className="inline-block w-full py-2.5 rounded-lg text-sm font-bold transition-colors"
                style={{ backgroundColor: `${mentorColor}15`, border: `1px solid ${mentorColor}44`, color: mentorColor }}>
                Acquista
              </Link>
            </div>
          ))}
        </div>

        {/* SCEGLI CHIARO O SCURO */}
        <div className={`${bgCard} border-2 ${borderAccent} rounded-2xl p-6 text-center mb-6`} data-testid="gift-style-chooser">
          <p className="font-mono text-xs uppercase tracking-wider mb-4" style={{ color: mentorColor }}>Personalizza il tuo regalo</p>
          <h3 className={`text-xl font-black mb-3 ${textMain}`}>Il tuo adesivo, la tua T-shirt, la tua penna: <span style={{ color: mentorColor }}>Chiaro o Scuro?</span></h3>
          <p className={`text-xs mb-5 ${textSub}`}>Scegli lo stile del tuo merchandising — come il sito, anche i regali hanno due anime.</p>
          <div className="flex justify-center gap-4">
            <button data-testid="gift-dark" onClick={() => setGiftStyle('dark')}
              className={`w-36 py-4 rounded-xl border-2 transition-all ${giftStyle === 'dark' ? 'border-[#00FF41] scale-105' : 'border-zinc-700 hover:border-zinc-500'}`}
              style={{ backgroundColor: '#0A0A0A' }}>
              <span className="font-mono font-black text-lg text-[#00FF41]" style={{ textShadow: '0 0 8px rgba(0,255,65,0.4)' }}>Matrix</span>
              <p className="text-zinc-500 text-[10px] mt-1">Sfondo nero, logo verde</p>
              {giftStyle === 'dark' && <p className="text-[#00FF41] text-[10px] mt-1 font-bold">Selezionato</p>}
            </button>
            <button data-testid="gift-light" onClick={() => setGiftStyle('light')}
              className={`w-36 py-4 rounded-xl border-2 transition-all ${giftStyle === 'light' ? 'border-[#FF6B35] scale-105' : 'border-[#E2DED6] hover:border-[#C4532D]'}`}
              style={{ backgroundColor: '#FAF9F6' }}>
              <span className="font-black text-lg text-[#FF6B35]">Chiaro</span>
              <p className="text-[#8A7A62] text-[10px] mt-1">Sfondo chiaro, logo arancione</p>
              {giftStyle === 'light' && <p className="text-[#FF6B35] text-[10px] mt-1 font-bold">Selezionato</p>}
            </button>
          </div>
        </div>

        <BrandDivider />

        {/* DIVENTA SOCIO */}
        <div className={`${bgCard} border-2 ${borderAccent} rounded-2xl p-8 text-center mb-10`}>
          <p className="font-mono text-xs uppercase tracking-[0.2em] mb-4" style={{ color: mentorColor }}>Alternativa alla T-Shirt</p>
          <h2 className={`text-2xl md:text-3xl font-black mb-4 ${textMain}`}>
            Diventa <span style={{ color: mentorColor }}>Socio</span> del Covo
          </h2>
          <p className={`text-sm max-w-xl mx-auto mb-4 ${textSub}`}>
            Vuoi sostenere il progetto <strong className={textMain} style={{ fontFamily: "'Georgia', serif" }}>
            "un Μέντωρ per tutti, dappertutto!"</strong>?
            Diventa socio e invece della T-shirt chiedi l'adesivo QR Code Stencil
            + gli accessi per partecipare ai <strong className={textMain}>corsi e dibattiti online</strong> al{' '}
            <strong className="font-mono font-black" style={{ color: mentorColor }}>&lt;/AI_covo.tech&gt;</strong>
            <sup style={{ fontSize: '0.6em', fontFamily: 'Georgia, serif', color: mentorColor }}>&reg;</sup>
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/dona" data-testid="mentor-socio-cta"
              className="inline-block px-8 py-3 font-black rounded-lg transition-colors text-sm"
              style={{ backgroundColor: mentorColor, color: isLight ? '#fff' : '#000' }}>
              Diventa Socio — 15€
            </Link>
            <Link to="/dona" className="inline-block px-8 py-3 border-2 font-black rounded-lg transition-colors text-sm"
              style={{ borderColor: mentorColor, color: mentorColor }}>
              Sponsor Aziendale — 999€
            </Link>
          </div>
          <p className={`text-[10px] font-mono mt-4 ${textMuted}`}>Tessera socio + Adesivo QR + Accesso corsi online + Dibattiti dal vivo</p>
        </div>

        <BrandDivider />

        {/* COPIA-INCOLLA ® */}
        <div className={`${bgCard} border rounded-xl p-5 text-center mb-8`}>
          <p className={`text-xs mb-3 ${textSub}`}>Copia e incolla il marchio registrato ovunque ti serva:</p>
          <div className="flex flex-col gap-2 items-center">
            <button onClick={() => { const t='</AI_covo.tech>®'; try{navigator.clipboard.writeText(t).then(()=>alert('Copiato!')).catch(()=>{const a=document.createElement('textarea');a.value=t;document.body.appendChild(a);a.select();document.execCommand('copy');document.body.removeChild(a);alert('Copiato!')})}catch(e){const a=document.createElement('textarea');a.value=t;document.body.appendChild(a);a.select();document.execCommand('copy');document.body.removeChild(a);alert('Copiato!')} }}
              data-testid="copy-brand"
              className="px-4 py-2 rounded-lg font-mono font-bold text-sm transition-colors"
              style={{ backgroundColor: `${mentorColor}15`, border: `1px solid ${mentorColor}44`, color: mentorColor }}>
              &lt;/AI_covo.tech&gt;® — clicca per copiare
            </button>
            <button onClick={() => { const t='®'; try{navigator.clipboard.writeText(t).then(()=>alert('Copiato!')).catch(()=>{const a=document.createElement('textarea');a.value=t;document.body.appendChild(a);a.select();document.execCommand('copy');document.body.removeChild(a);alert('Copiato!')})}catch(e){const a=document.createElement('textarea');a.value=t;document.body.appendChild(a);a.select();document.execCommand('copy');document.body.removeChild(a);alert('Copiato!')} }}
              className={`px-4 py-2 rounded-lg font-mono text-xs transition-colors ${isLight ? 'bg-[#F0EDE5] border border-[#E2DED6] text-[#5A6B80]' : 'bg-zinc-900 border border-zinc-700 text-zinc-400'}`}>
              ® — solo il simbolo
            </button>
          </div>
        </div>

        <div className="text-center">
          <p className={`text-[10px] font-mono ${textMuted}`}>
            <strong className="font-mono font-black" style={{ color: mentorColor }}>&lt;/AI_covo.tech&gt;</strong><sup style={{ fontSize: '0.6em', fontFamily: 'Georgia, serif', color: mentorColor }}>&reg;</sup>
            {' '}— DIGITAL HUB SOCIALE — Colle di Val d'Elsa (SI) — Visionari in Missione
          </p>
        </div>
      </div>
    </main>
  );
};

export default MentorPage;
